import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Tab, Tabs, Modal, Button, Spinner, Alert } from 'react-bootstrap';
import { DocumentsStyles } from '../styles/DocumentsStyled';

// Tabs
import ToBeCalledTab from './ToBeCalledTab';
import CalledTab from './CalledTab';
import PresetsTab from './PresetsTab';

// helpers
import { getTeamIdFromURL } from '../../../../../_helpers';
import {
    fetchContactsAction,
    fetchPresetsAction,
    fetchCallsAction,
    createCallAction,
    initiateCallAction,
    endCallAction,
    bargeIntoCallAction,
    listenToCallAction,
    createContactAction,
    bulkCreateContactsAction,
    setCallCenterError,
    clearCallCenterError,
    setActiveCall,
    clearActiveCall,
    fetchLabelsAction,
} from '../../../../../_redux/actions/ActionCallCenter';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import LabelsTab from './LabelsTab';

function Component({
    currUser,
    callCenter,
    fetchContactsAction,
    fetchPresetsAction,
    fetchLabelsAction,
    fetchCallsAction,
    createCallAction,
    initiateCallAction,
    endCallAction,
    bargeIntoCallAction,
    listenToCallAction,
    createContactAction,
    bulkCreateContactsAction,
    setCallCenterError,
    clearCallCenterError,
    setActiveCall,
    clearActiveCall,
}) {
    const teamId = getTeamIdFromURL();
    const [key, setKey] = useState('to-be-called');
    const [showActiveCallModal, setShowActiveCallModal] = useState(false);
    const [actionLoading, setActionLoading] = useState({
        hangup: false,
        listen: false,
        barge: false,
    });

    const { activeCall, callConnection, calls, loading, error } = callCenter || {};

    // Fetch call center data when component mounts
    useEffect(() => {
        if (teamId) {
            fetchCallCenterData();
        }
    }, [teamId]);

    const fetchCallCenterData = async () => {
        try {
            await Promise.all([fetchContactsAction(teamId), fetchPresetsAction(teamId), fetchLabelsAction(teamId)]);
        } catch (error) {
            console.error('Error fetching call center data:', error);
        }
    };

    const handleCreateCall = async (callData) => {
        try {
            if (!teamId) {
                throw new Error('Team not loaded');
            }

            const response = await createCallAction({
                ...callData,
                team: teamId,
                dialedByUser: currUser.id,
            });

            return response;
        } catch (error) {
            console.error('Error creating call:', error);
            throw error;
        }
    };

    const handleInitiateCall = async (callId) => {
        try {
            const response = await initiateCallAction(callId);

            // Open active call modal
            setShowActiveCallModal(true);
            return response;
        } catch (error) {
            console.error('Error initiating call:', error);
            throw error;
        }
    };

    // Active call actions
    const handleHangupActive = async () => {
        if (!activeCall) return;

        setActionLoading((prev) => ({ ...prev, hangup: true }));
        try {
            const callId = activeCall.id || activeCall._id;
            await endCallAction(callId);
            setShowActiveCallModal(false);
        } catch (error) {
            console.error('Error hanging up call:', error);
        } finally {
            setActionLoading((prev) => ({ ...prev, hangup: false }));
        }
    };

    const handleListenInActive = async () => {
        if (!activeCall) return;

        setActionLoading((prev) => ({ ...prev, listen: true }));
        try {
            const callId = activeCall.id || activeCall._id;
            await listenToCallAction(callId);
        } catch (error) {
            console.error('Error listening to call:', error);
        } finally {
            setActionLoading((prev) => ({ ...prev, listen: false }));
        }
    };

    const handleBargeInActive = async () => {
        if (!activeCall) return;

        setActionLoading((prev) => ({ ...prev, barge: true }));
        try {
            const callId = activeCall.id || activeCall._id;
            await bargeIntoCallAction(callId);
        } catch (error) {
            console.error('Error barging into call:', error);
        } finally {
            setActionLoading((prev) => ({ ...prev, barge: false }));
        }
    };

    // Contact management
    const handleCreateContact = async (contactData) => {
        try {
            const response = await createContactAction({
                ...contactData,
                team: teamId,
            });
            return response;
        } catch (error) {
            console.error('Error creating contact:', error);
            throw error;
        }
    };

    const handleBulkCreateContacts = async (contactsData) => {
        try {
            const response = await bulkCreateContactsAction(contactsData, teamId);
            return response;
        } catch (error) {
            console.error('Error bulk creating contacts:', error);
            throw error;
        }
    };

    // Close active call modal
    const handleCloseActiveCallModal = () => {
        setShowActiveCallModal(false);
        if (activeCall?.callStatus === 'completed') {
            clearActiveCall();
        }
    };

    return (
        <div css={DocumentsStyles}>
            <h1 className="mb-4">Call Center</h1>

            {/* {error && (
                <Alert variant="danger" className="mb-3">
                    <strong>Error:</strong> {error}
                    <Button variant="outline-danger" size="sm" className="ms-2" onClick={clearCallCenterError}>
                        Dismiss
                    </Button>
                </Alert>
            )} */}

            <Tabs id="call-center-tabs" activeKey={key} onSelect={(k) => setKey(k)} className="mb-3">
                <Tab eventKey="to-be-called" title="To Be Called">
                    {key === 'to-be-called' && (
                        <ToBeCalledTab
                            loading={loading}
                            onCreateContact={handleCreateContact}
                            onCreateCall={handleCreateCall}
                            onInitiateCall={handleInitiateCall}
                            onBulkCreateContacts={handleBulkCreateContacts}
                        />
                    )}
                </Tab>
                <Tab eventKey="called" title="Called">
                    {key === 'called' && (
                        <CalledTab
                            calls={calls}
                            loading={loading}
                            onBargeIn={handleBargeInActive}
                            onListenIn={handleListenInActive}
                            onShowCallDetails={(call) => {
                                setActiveCall(call);
                                setShowActiveCallModal(true);
                            }}
                        />
                    )}
                </Tab>
                <Tab eventKey="presets" title="Presets">
                    {key === 'presets' && <PresetsTab />}
                </Tab>
                {/* <Tab eventKey="labels" title="Labels">
                    {key === 'labels' && <LabelsTab />}
                </Tab> */}
            </Tabs>

            {/* Active Call Modal */}
            <Modal show={showActiveCallModal} onHide={handleCloseActiveCallModal} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Active Call
                        {activeCall && (
                            <span
                                className={`badge ms-2 ${
                                    activeCall.callStatus === 'in_progress'
                                        ? 'bg-success'
                                        : activeCall.callStatus === 'ringing'
                                          ? 'bg-warning'
                                          : 'bg-secondary'
                                }`}
                            >
                                {activeCall.callStatus?.replace('_', ' ') || 'Unknown'}
                            </span>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!activeCall ? (
                        <div className="text-muted">No active call</div>
                    ) : (
                        <>
                            <div className="mb-3">
                                <strong>Contact:</strong> {activeCall?.contact?.name || 'Unknown'}(
                                {activeCall?.contact?.phone || activeCall.contact?.phone})
                            </div>
                            <div className="mb-3">
                                <strong>Preset:</strong> {activeCall.preset?.title || 'No preset'}
                            </div>
                            <div className="mb-3">
                                <strong>Started:</strong>{' '}
                                {new Date(activeCall.startedAt || activeCall.createdAt).toLocaleString()}
                            </div>

                            {callConnection && (
                                <Alert variant="info" className="mt-3">
                                    <h6>Connection Established</h6>
                                    <div className="small">
                                        <strong>Conference:</strong> {callConnection?.conferenceName || '-'}
                                        <br />
                                        <strong>Token ready for use</strong>
                                    </div>
                                </Alert>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {/* // TODO: Add listen in and barge in */}
                    {/* <Button
                        variant="outline-secondary"
                        onClick={handleListenInActive}
                        disabled={actionLoading.listen || !activeCall || activeCall.callStatus !== 'in_progress'}
                    >
                        {actionLoading.listen ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-1" /> Connecting...
                            </>
                        ) : (
                            'Listen In'
                        )}
                    </Button> */}
                    {/* <Button
                        variant="outline-primary"
                        onClick={handleBargeInActive}
                        disabled={actionLoading.barge || !activeCall || activeCall.callStatus !== 'in_progress'}
                    >
                        {actionLoading.barge ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-1" /> Connecting...
                            </>
                        ) : (
                            'Barge In'
                        )}
                    </Button> */}
                    <Button
                        variant="outline-danger"
                        onClick={handleHangupActive}
                        disabled={actionLoading.hangup || !activeCall}
                    >
                        {actionLoading.hangup ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-1" /> Hanging up...
                            </>
                        ) : (
                            'Hang Up'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

const mapStateToProps = (state) => ({
    callCenter: state.callCenter,
    currUser: state.user.user,
});

const mapDispatchToProps = {
    fetchContactsAction,
    fetchPresetsAction,
    fetchLabelsAction,
    fetchCallsAction,
    createCallAction,
    initiateCallAction,
    endCallAction,
    bargeIntoCallAction,
    listenToCallAction,
    createContactAction,
    bulkCreateContactsAction,
    setCallCenterError,
    clearCallCenterError,
    setActiveCall,
    clearActiveCall,
};

const CallCenterComponent = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default connect(mapStateToProps, mapDispatchToProps)(CallCenterComponent);
